<template>
  <div class="p-4">
    <div
      v-for="(key, value) in configs"
      :key="key"
      class="w-full py-3 px-4 focus:outline-none focus:bg-blue-100 focus:shadow-lg border-l-4 bg-white rounded-lg shadow min-h-24 mr-3"
    >
      <div>
        <div>
          <p class="font-small text-gray-600">
            Enable or disable pending payment banner to this tenant
          </p>
        </div>
        <div class="flex gap-2 items-center">
          <p class="font-medium text-gray-700">{{ value }} --- {{ key }}</p>
          <ramro-button
            :variant="getButtonColor(key)"
            v-if="value === 'payment_banner'"
            @click="togglePaymentBanner(key)"
          >
            <slot>
              {{ key === "enabled" ? "disable" : "enable" }}
            </slot>
          </ramro-button>
        </div>
      </div>
    </div>
    <div
      class="mt-4 w-full py-3 px-4 focus:outline-none focus:bg-blue-100 focus:shadow-lg border-l-4 bg-white rounded-lg shadow min-h-24 mr-3"
    >
      <div>
        <div>
          <p class="font-small text-gray-600">Clear Cache of Tenant</p>
        </div>
        <div class="flex gap-2 items-center">
          <ramro-button variant="primary" @click="clearTenantCache()">
            <slot> Clear Cache </slot>
          </ramro-button>
        </div>
      </div>
    </div>

    <!-- WhatsApp Section -->
    <div
      class="mt-4 w-full py-3 px-4 focus:outline-none focus:bg-blue-100 focus:shadow-lg border-l-4 bg-white rounded-lg shadow min-h-24 mr-3"
    >
      <div>
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <p class="font-small text-gray-600">WhatsApp</p>
            <!-- Status Badge -->
            <span
              v-if="whatsappStatus"
              class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
              :class="getStatusBadgeClass(whatsappStatus)"
            >
              <span
                class="w-2 h-2 rounded-full mr-1.5"
                :class="getStatusDotClass(whatsappStatus)"
              ></span>
              {{
                whatsappStatus.charAt(0).toUpperCase() + whatsappStatus.slice(1)
              }}
            </span>
          </div>
          <button
            @click="toggleWhatsAppSection"
            class="text-gray-400 hover:text-gray-600"
          >
            <svg
              class="w-5 h-5 transform transition-transform"
              :class="{ 'rotate-180': !showWhatsAppSection }"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 15l7-7 7 7"
              ></path>
            </svg>
          </button>
        </div>

        <div v-show="showWhatsAppSection" class="mt-4 space-y-4">
          <!-- Enable WhatsApp Toggle -->
          <div class="flex gap-3 items-center">
            <div class="relative inline-block">
              <input
                type="checkbox"
                v-model="isWhatsAppEnabled"
                @change="toggleWhatsApp"
                class="sr-only"
                id="whatsapp-toggle"
              />
              <label
                for="whatsapp-toggle"
                class="toggle-switch cursor-pointer"
                :class="{ 'toggle-switch-on': isWhatsAppEnabled }"
              >
                <span class="toggle-slider"></span>
              </label>
            </div>
            <p class="font-medium text-gray-700">Enable WhatsApp</p>
          </div>

          <!-- Attachment Section -->
          <div class="relative mb-6">
            <!-- Attachment Label positioned on the border -->
            <div
              class="absolute -top-2 left-8 bg-white px-4 py-1 text-sm font-medium text-gray-700 z-10 shadow-sm"
              style="
                margin: 0 0 20px 20px;
                background-color: #4b5563;
                color: #fff;
              "
            >
              Attachment
            </div>

            <!-- Bordered container for file upload -->
            <div class="border border-gray-300 rounded-lg p-4 space-y-3 mt-2">
              <!-- Upload Invoice -->
              <div
                class="flex items-center gap-3 p-3 border border-dashed border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                @click="triggerFileUpload"
                style="margin-top: 50px"
              >
                <div
                  class="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center"
                >
                  <svg
                    class="w-5 h-5 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    ></path>
                  </svg>
                </div>
                <div class="flex-1">
                  <p class="text-sm font-medium text-gray-900">
                    Upload Invoice
                  </p>
                  <p class="text-xs text-gray-500">
                    Click to upload PDF, JPG, PNG files
                  </p>
                </div>
                <input
                  ref="fileInput"
                  type="file"
                  class="hidden"
                  @change="handleFileUpload"
                  accept=".pdf,.jpg,.jpeg,.png"
                />
              </div>

              <!-- Show uploaded file if exists -->
              <div
                v-if="uploadedFileName"
                class="flex items-center gap-2 text-sm text-green-600 bg-green-50 p-2 rounded"
              >
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 13l4 4L19 7"
                  ></path>
                </svg>
                {{ uploadedFileName }}
              </div>
            </div>
          </div>

          <!-- Save/Discard Buttons -->
          <div class="flex gap-3 justify-end pt-4 border-t border-gray-200">
            <ramro-button
              variant="secondary"
              @click="discardWhatsAppChanges"
              class="px-6 py-2"
            >
              Discard
            </ramro-button>
            <ramro-button
              variant="primary"
              @click="saveWhatsAppChanges"
              class="px-6 py-2"
            >
              Save
            </ramro-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import RamroButton from "@/components/Button/Button.vue";

export default {
  name: "TenantConfig",
  components: {
    RamroButton,
  },
  props: ["tenantId"],
  data() {
    return {
      configs: [],
      showWhatsAppSection: true,
      isWhatsAppEnabled: false,
      originalWhatsAppEnabled: false, // Track original state
      uploadedFileName: null,
      uploadedFile: null, // Store the actual file
      whatsappStatus: null, // Store current WhatsApp status from API
    };
  },
  created() {
    this.fetchConfig();
  },
  methods: {
    getButtonColor(key) {
      return key === "enabled" ? "danger" : "success";
    },
    fetchConfig() {
      axios
        .get(`/tenants/${this.$route.params.id}/config`)
        .then((response) => {
          this.configs =
            response.data && response.data.data && response.data.data.meta;

          // Initialize WhatsApp state from config
          if (this.configs && this.configs.whatsapp_enabled) {
            this.isWhatsAppEnabled =
              this.configs.whatsapp_enabled === "enabled";
            this.originalWhatsAppEnabled = this.isWhatsAppEnabled; // Store original state
          }

          // Fetch WhatsApp status separately
          this.fetchWhatsAppStatus();
        })
        .catch((err) => {
          this.$toasted.error(
            err.response.data.message || "Oops, Something went wrong.."
          );
        });
    },
    togglePaymentBanner(status) {
      const newStatus = status === "enabled" ? "disabled" : "enabled";
      axios
        .put(`/tenants/${this.$route.params.id}/${newStatus}`)
        .then((response) => {
          this.$toasted.success(
            response.data.message || "successfully updated"
          );
        })
        .catch((err) => {
          this.$toasted.error(
            err.response.data.message || "Oops, Something went wrong.."
          );
        })
        .finally(() => {
          this.clearTenantCache();
          this.$router.go(0);
        });
    },
    clearTenantCache() {
      axios
        .get(`/tenants/${this.$route.params.id}/cache-clear`)
        .then((response) => {
          this.configs =
            response.data && response.data.data && response.data.data.meta;
        })
        .catch((err) => {
          this.$toasted.error(
            err.response.data.message || "Oops, Something went wrong.."
          );
        });
    },
    toggleWhatsAppSection() {
      this.showWhatsAppSection = !this.showWhatsAppSection;
    },

    toggleWhatsApp() {
      // Just toggle the local state, no API call
      // The actual save will happen when user clicks Save button
    },

    triggerFileUpload() {
      this.$refs.fileInput.click();
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.uploadedFileName = file.name;
        this.uploadedFile = file;
      }
    },

    saveWhatsAppChanges() {
      const status = this.isWhatsAppEnabled ? "enabled" : "disabled";

      // Check if enabling WhatsApp and no attachment is uploaded
      if (status === "enabled" && !this.uploadedFile) {
        this.$toasted.error("Attachment is required to enable WhatsApp.");
        return;
      }

      // Create FormData to send file along with status
      const formData = new FormData();
      formData.append("status", status);
      if (this.uploadedFile) {
        formData.append("attachment", this.uploadedFile);
      }

      axios
        .post(`/tenants/${this.$route.params.id}/toggle-whatsapp`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response) => {
          this.$toasted.success(
            response.data.message || "WhatsApp status updated successfully"
          );
          // Update original state to current state
          this.originalWhatsAppEnabled = this.isWhatsAppEnabled;
          // Update the status badge
          this.whatsappStatus = status;
          // Refresh the config to ensure UI is in sync
          this.fetchConfig();
        })
        .catch((err) => {
          this.$toasted.error(
            err.response?.data?.message || "Failed to update WhatsApp status"
          );
        });
    },

    discardWhatsAppChanges() {
      // Reset to original state
      this.isWhatsAppEnabled = this.originalWhatsAppEnabled;
      // Clear uploaded file
      this.uploadedFileName = null;
      this.uploadedFile = null;
      // Reset file input
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
      this.$toasted.info("Changes discarded");
    },

    fetchWhatsAppStatus() {
      axios
        .get(`/tenants/${this.$route.params.id}/whatsapp-config`)
        .then((response) => {
          if (response.data && response.data.data) {
            this.whatsappStatus = response.data.data.whatsapp_status;

            // Sync toggle state with WhatsApp status
            this.isWhatsAppEnabled = this.whatsappStatus === "enabled";
            this.originalWhatsAppEnabled = this.isWhatsAppEnabled;
          }
        })
        .catch(() => {
          alert("WhatsApp status fetch failed. Please try again.");
        });
    },

    getStatusBadgeClass(status) {
      return {
        "bg-green-100 text-green-800": status === "enabled",
        "bg-gray-100 text-gray-800": status === "disabled",
        "bg-yellow-100 text-yellow-800": status === "pending",
      };
    },

    getStatusDotClass(status) {
      return {
        "bg-green-400": status === "enabled",
        "bg-gray-400": status === "disabled",
        "bg-yellow-400": status === "pending",
      };
    },
  },
};
</script>

<style scoped>
/* Hide the default checkbox */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Toggle switch container */
.toggle-switch {
  display: inline-block;
  width: 44px;
  height: 24px;
  background-color: #d1d5db;
  border-radius: 12px;
  position: relative;
  transition: background-color 0.3s ease;
}

/* Toggle switch when ON */
.toggle-switch-on {
  background-color: #3b82f6;
}

/* Toggle slider (the white circle) */
.toggle-slider {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  background-color: white;
  border-radius: 50%;
  transition: transform 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* Move slider when toggle is ON */
.toggle-switch-on .toggle-slider {
  transform: translateX(20px);
}

/* Hover effect */
.toggle-switch:hover .toggle-slider {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}
</style>
